export type TimesheetStatus = 'draft' | 'submitted' | 'approved' | 'rejected';
export type EntryCategory = 'consulting' | 'research' | 'travel' | 'admin' | 'reporting';

export type CostLineType = 'consulting_fee' | 'travel' | 'meal' | 'accommodation' | 'transport' | 'per_diem' | 'other';
export type FeeUnit = 'hourly' | 'daily' | 'weekly' | 'monthly';

export interface CostLine {
  id: string;
  type: CostLineType;
  feeUnit?: FeeUnit;
  description?: string;
  quantity: number;
  unitRate: number;
  amount: number;
  currency: 'USD' | 'EUR' | 'TRY';
}

export const COST_LINE_LABELS: Record<CostLineType, string> = {
  consulting_fee: 'Consulting Fee',
  travel: 'Travel',
  meal: 'Meal & Food',
  accommodation: 'Accommodation',
  transport: 'Local Transport',
  per_diem: 'Per Diem',
  other: 'Other Expense',
};

export const COST_LINE_ICONS: Record<CostLineType, string> = {
  consulting_fee: 'ri-briefcase-4-line',
  travel: 'ri-flight-takeoff-line',
  meal: 'ri-restaurant-line',
  accommodation: 'ri-hotel-line',
  transport: 'ri-taxi-line',
  per_diem: 'ri-coin-line',
  other: 'ri-more-2-line',
};

export const COST_LINE_COLORS: Record<CostLineType, string> = {
  consulting_fee: 'bg-teal-100 text-teal-700',
  travel: 'bg-amber-100 text-amber-700',
  meal: 'bg-orange-100 text-orange-700',
  accommodation: 'bg-slate-100 text-slate-700',
  transport: 'bg-stone-100 text-stone-700',
  per_diem: 'bg-green-100 text-green-700',
  other: 'bg-gray-100 text-gray-600',
};

export const FEE_UNIT_LABELS: Record<FeeUnit, string> = {
  hourly: 'Hourly',
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
};

export const FEE_UNIT_QTY_LABELS: Record<FeeUnit, string> = {
  hourly: 'hrs',
  daily: 'days',
  weekly: 'wks',
  monthly: 'mos',
};

export const FEE_UNIT_RATE_SUFFIX: Record<FeeUnit, string> = {
  hourly: '/hr',
  daily: '/day',
  weekly: '/wk',
  monthly: '/mo',
};

export interface TimesheetAttachment {
  id: string;
  name: string;
  url: string;
  fileType: string;
  sizeKB: number;
  uploadedAt: string;
}

export interface TimesheetEntry {
  id: string;
  consultantId: string;
  projectId: string;
  date: string;
  hours: number;
  taskDescription: string;
  category: EntryCategory;
  attachments: TimesheetAttachment[];
  notes?: string;
  /** Legacy single-cost fields — kept for backward compat */
  hourlyRate?: number;
  costAmount?: number;
  /** New: multiple cost/expense lines per entry */
  costLines?: CostLine[];
}

/** Total cost for an entry (from costLines if present, else legacy costAmount) */
export function entryCostTotal(entry: TimesheetEntry): number {
  if (entry.costLines && entry.costLines.length > 0) {
    return entry.costLines.reduce((s, l) => s + l.amount, 0);
  }
  return entry.costAmount ?? 0;
}

export interface MonthlyTimesheet {
  id: string;
  consultantId: string;
  month: string;
  entries: TimesheetEntry[];
  status: TimesheetStatus;
  submittedAt?: string;
  reviewedAt?: string;
  reviewedBy?: string;
  rejectionReason?: string;
  archivedAt?: string;
  archivedById?: string;
  archivedByName?: string;
  createdAt: string;
}

export const CATEGORY_LABELS: Record<EntryCategory, string> = {
  consulting: 'Consulting',
  research: 'Research',
  travel: 'Travel',
  admin: 'Admin',
  reporting: 'Reporting',
};

export const CATEGORY_COLORS: Record<EntryCategory, string> = {
  consulting: 'bg-teal-100 text-teal-700',
  research: 'bg-blue-100 text-blue-700',
  travel: 'bg-violet-100 text-violet-700',
  admin: 'bg-slate-100 text-slate-600',
  reporting: 'bg-green-100 text-green-700',
};

export const STATUS_COLORS: Record<TimesheetStatus, string> = {
  draft: 'bg-slate-100 text-slate-600',
  submitted: 'bg-teal-100 text-teal-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-600',
};

export const mockTimesheets: MonthlyTimesheet[] = [
  {
    id: 'ts-001',
    consultantId: 'admin-root',
    month: '2026-01',
    status: 'approved',
    submittedAt: '2026-01-31',
    reviewedAt: '2026-02-03',
    reviewedBy: 'admin-root',
    createdAt: '2026-01-02',
    entries: [
      { id: 'e-001', consultantId: 'admin-root', projectId: 'proj-009', date: '2026-01-06', hours: 6, taskDescription: 'Wind energy site assessment and technical review', category: 'consulting', attachments: [{ id: 'a-001', name: 'site_assessment_jan_2026.pdf', url: '#', fileType: 'pdf', sizeKB: 1240, uploadedAt: '2026-01-06' }] },
      { id: 'e-002', consultantId: 'admin-root', projectId: 'proj-010', date: '2026-01-07', hours: 4, taskDescription: 'MENA competitor intelligence research', category: 'research', attachments: [] },
      { id: 'e-003', consultantId: 'admin-root', projectId: 'proj-009', date: '2026-01-08', hours: 5, taskDescription: 'Grid connection cost estimation', category: 'consulting', attachments: [] },
      { id: 'e-004', consultantId: 'admin-root', projectId: 'proj-010', date: '2026-01-09', hours: 3, taskDescription: 'Partner ecosystem mapping workshop', category: 'consulting', attachments: [] },
      { id: 'e-005', consultantId: 'admin-root', projectId: 'proj-009', date: '2026-01-13', hours: 7, taskDescription: 'Financial modeling and CAPEX scenarios', category: 'reporting', attachments: [{ id: 'a-002', name: 'capex_model_v2.xlsx', url: '#', fileType: 'xlsx', sizeKB: 380, uploadedAt: '2026-01-13' }] },
      { id: 'e-006', consultantId: 'admin-root', projectId: 'proj-010', date: '2026-01-14', hours: 4, taskDescription: 'Go-to-market strategy draft review', category: 'consulting', attachments: [] },
      { id: 'e-007', consultantId: 'admin-root', projectId: 'proj-009', date: '2026-01-15', hours: 2, taskDescription: 'Travel to Aegean site visit', category: 'travel', attachments: [] },
      { id: 'e-008', consultantId: 'admin-root', projectId: 'proj-009', date: '2026-01-20', hours: 5, taskDescription: 'Bankable financial model preparation', category: 'reporting', attachments: [] },
      { id: 'e-009', consultantId: 'admin-root', projectId: 'proj-010', date: '2026-01-21', hours: 3, taskDescription: 'Client presentation preparation', category: 'admin', attachments: [] },
      { id: 'e-010', consultantId: 'admin-root', projectId: 'proj-009', date: '2026-01-27', hours: 6, taskDescription: 'Environmental impact section finalization', category: 'consulting', attachments: [{ id: 'a-003', name: 'environmental_section.pdf', url: '#', fileType: 'pdf', sizeKB: 890, uploadedAt: '2026-01-27' }] },
      { id: 'e-011', consultantId: 'admin-root', projectId: 'proj-010', date: '2026-01-28', hours: 4, taskDescription: 'Competitive positioning document', category: 'reporting', attachments: [] },
    ],
  },
  {
    id: 'ts-002',
    consultantId: 'admin-root',
    month: '2026-02',
    status: 'approved',
    submittedAt: '2026-02-28',
    reviewedAt: '2026-03-02',
    reviewedBy: 'admin-root',
    createdAt: '2026-02-03',
    entries: [
      { id: 'e-020', consultantId: 'admin-root', projectId: 'proj-009', date: '2026-02-03', hours: 8, taskDescription: 'Final feasibility study draft completion', category: 'reporting', attachments: [{ id: 'a-010', name: 'feasibility_draft_final.pdf', url: '#', fileType: 'pdf', sizeKB: 2400, uploadedAt: '2026-02-03' }] },
      { id: 'e-021', consultantId: 'admin-root', projectId: 'proj-010', date: '2026-02-04', hours: 5, taskDescription: 'MENA market entry presentation to client', category: 'consulting', attachments: [] },
      { id: 'e-022', consultantId: 'admin-root', projectId: 'proj-009', date: '2026-02-10', hours: 6, taskDescription: 'Permit risk assessment appendix', category: 'consulting', attachments: [] },
      { id: 'e-023', consultantId: 'admin-root', projectId: 'proj-010', date: '2026-02-11', hours: 4, taskDescription: 'Final positioning whitepaper review', category: 'reporting', attachments: [] },
      { id: 'e-024', consultantId: 'admin-root', projectId: 'proj-009', date: '2026-02-18', hours: 5, taskDescription: 'Stakeholder review meeting and revisions', category: 'consulting', attachments: [] },
      { id: 'e-025', consultantId: 'admin-root', projectId: 'proj-010', date: '2026-02-19', hours: 3, taskDescription: 'Partner ecosystem final report delivery', category: 'reporting', attachments: [] },
    ],
  },
  {
    id: 'ts-003',
    consultantId: 'admin-root',
    month: '2026-03',
    status: 'submitted',
    submittedAt: '2026-03-25',
    createdAt: '2026-03-03',
    entries: [
      { id: 'e-030', consultantId: 'admin-root', projectId: 'proj-009', date: '2026-03-03', hours: 6, taskDescription: 'Wind resource analysis final review', category: 'consulting', attachments: [] },
      { id: 'e-031', consultantId: 'admin-root', projectId: 'proj-010', date: '2026-03-04', hours: 4, taskDescription: 'Go-to-market execution support', category: 'consulting', attachments: [] },
      { id: 'e-032', consultantId: 'admin-root', projectId: 'proj-009', date: '2026-03-10', hours: 5, taskDescription: 'Investor readiness documentation', category: 'reporting', attachments: [{ id: 'a-020', name: 'investor_pack_mar26.pdf', url: '#', fileType: 'pdf', sizeKB: 1100, uploadedAt: '2026-03-10' }] },
      { id: 'e-033', consultantId: 'admin-root', projectId: 'proj-010', date: '2026-03-11', hours: 3, taskDescription: 'Client check-in and revised scope discussion', category: 'admin', attachments: [] },
    ],
  },
  {
    id: 'ts-004',
    consultantId: 'cons-001',
    month: '2026-01',
    status: 'approved',
    submittedAt: '2026-01-30',
    reviewedAt: '2026-02-05',
    reviewedBy: 'admin-root',
    createdAt: '2026-01-03',
    entries: [
      { id: 'e-040', consultantId: 'cons-001', projectId: 'proj-009', date: '2026-01-06', hours: 7, taskDescription: 'Turbine layout optimization analysis', category: 'consulting', attachments: [{ id: 'a-040', name: 'turbine_layout_v1.pdf', url: '#', fileType: 'pdf', sizeKB: 1500, uploadedAt: '2026-01-06' }] },
      { id: 'e-041', consultantId: 'cons-001', projectId: 'proj-009', date: '2026-01-08', hours: 5, taskDescription: 'Environmental impact scoping study', category: 'research', attachments: [] },
      { id: 'e-042', consultantId: 'cons-001', projectId: 'proj-009', date: '2026-01-13', hours: 8, taskDescription: 'Grid integration study and substation costing', category: 'consulting', attachments: [] },
      { id: 'e-043', consultantId: 'cons-001', projectId: 'proj-009', date: '2026-01-20', hours: 6, taskDescription: 'O&M cost benchmarking report', category: 'reporting', attachments: [{ id: 'a-041', name: 'om_benchmark_jan.xlsx', url: '#', fileType: 'xlsx', sizeKB: 420, uploadedAt: '2026-01-20' }] },
      { id: 'e-044', consultantId: 'cons-001', projectId: 'proj-009', date: '2026-01-27', hours: 5, taskDescription: 'Technical review presentation', category: 'consulting', attachments: [] },
    ],
  },
  {
    id: 'ts-005',
    consultantId: 'cons-001',
    month: '2026-02',
    status: 'submitted',
    submittedAt: '2026-02-27',
    createdAt: '2026-02-03',
    entries: [
      { id: 'e-050', consultantId: 'cons-001', projectId: 'proj-009', date: '2026-02-03', hours: 7, taskDescription: 'Wind resource assessment final report', category: 'reporting', attachments: [{ id: 'a-050', name: 'wind_resource_final.pdf', url: '#', fileType: 'pdf', sizeKB: 3100, uploadedAt: '2026-02-03' }] },
      { id: 'e-051', consultantId: 'cons-001', projectId: 'proj-009', date: '2026-02-10', hours: 6, taskDescription: 'OPEX scenario modelling', category: 'consulting', attachments: [] },
      { id: 'e-052', consultantId: 'cons-001', projectId: 'proj-009', date: '2026-02-17', hours: 5, taskDescription: 'Permitting risk matrix completion', category: 'reporting', attachments: [] },
      { id: 'e-053', consultantId: 'cons-001', projectId: 'proj-009', date: '2026-02-24', hours: 4, taskDescription: 'Client review meeting preparation', category: 'admin', attachments: [] },
    ],
  },
  {
    id: 'ts-006',
    consultantId: 'cons-001',
    month: '2026-03',
    status: 'draft',
    createdAt: '2026-03-03',
    entries: [
      { id: 'e-060', consultantId: 'cons-001', projectId: 'proj-009', date: '2026-03-03', hours: 5, taskDescription: 'Final report quality review', category: 'reporting', attachments: [] },
      { id: 'e-061', consultantId: 'cons-001', projectId: 'proj-009', date: '2026-03-10', hours: 4, taskDescription: 'Client handover documentation', category: 'admin', attachments: [] },
    ],
  },
  {
    id: 'ts-007',
    consultantId: 'cons-002',
    month: '2026-02',
    status: 'approved',
    submittedAt: '2026-02-26',
    reviewedAt: '2026-03-01',
    reviewedBy: 'admin-root',
    createdAt: '2026-02-04',
    entries: [
      { id: 'e-070', consultantId: 'cons-002', projectId: 'proj-005', date: '2026-02-04', hours: 5, taskDescription: 'Policy framework alignment review', category: 'research', attachments: [] },
      { id: 'e-071', consultantId: 'cons-002', projectId: 'proj-005', date: '2026-02-11', hours: 4, taskDescription: 'Stakeholder consultation notes synthesis', category: 'reporting', attachments: [{ id: 'a-070', name: 'stakeholder_notes_feb.pdf', url: '#', fileType: 'pdf', sizeKB: 650, uploadedAt: '2026-02-11' }] },
      { id: 'e-072', consultantId: 'cons-002', projectId: 'proj-005', date: '2026-02-18', hours: 6, taskDescription: 'EU cohesion alignment gap analysis', category: 'consulting', attachments: [] },
      { id: 'e-073', consultantId: 'cons-002', projectId: 'proj-005', date: '2026-02-25', hours: 3, taskDescription: 'Progress report to client', category: 'reporting', attachments: [] },
    ],
  },
  {
    id: 'ts-008',
    consultantId: 'cons-002',
    month: '2026-03',
    status: 'rejected',
    submittedAt: '2026-03-20',
    reviewedAt: '2026-03-22',
    reviewedBy: 'admin-root',
    rejectionReason: 'Missing task descriptions for entries on March 12 and 19. Please revise and resubmit.',
    createdAt: '2026-03-04',
    entries: [
      { id: 'e-080', consultantId: 'cons-002', projectId: 'proj-005', date: '2026-03-04', hours: 4, taskDescription: 'Regional action plan final draft', category: 'reporting', attachments: [] },
      { id: 'e-081', consultantId: 'cons-002', projectId: 'proj-005', date: '2026-03-11', hours: 3, taskDescription: 'KPI framework review', category: 'consulting', attachments: [] },
      { id: 'e-082', consultantId: 'cons-002', projectId: 'proj-005', date: '2026-03-12', hours: 5, taskDescription: '', category: 'admin', attachments: [] },
      { id: 'e-083', consultantId: 'cons-002', projectId: 'proj-005', date: '2026-03-18', hours: 4, taskDescription: '', category: 'admin', attachments: [] },
    ],
  },
  {
    id: 'ts-009',
    consultantId: 'cons-003',
    month: '2026-03',
    status: 'draft',
    createdAt: '2026-03-05',
    entries: [
      { id: 'e-090', consultantId: 'cons-003', projectId: 'proj-010', date: '2026-03-05', hours: 6, taskDescription: 'Competitive landscape deep-dive for MENA', category: 'research', attachments: [] },
      { id: 'e-091', consultantId: 'cons-003', projectId: 'proj-010', date: '2026-03-12', hours: 5, taskDescription: 'Market positioning workshop facilitation', category: 'consulting', attachments: [] },
      { id: 'e-092', consultantId: 'cons-003', projectId: 'proj-010', date: '2026-03-19', hours: 4, taskDescription: 'Strategic options memo', category: 'reporting', attachments: [{ id: 'a-090', name: 'strategic_options_memo.docx', url: '#', fileType: 'docx', sizeKB: 220, uploadedAt: '2026-03-19' }] },
    ],
  },
];
